import config from '@/common/config';
import {
  MeasurementBuilderFormData,
  Metric,
  MetricKeys,
  PromiseResponse
} from '@/common/global.interfaces';
import { dataToSeries } from '@/common/helpers/data-visualisations-utils';
import * as dateUtils from '@/common/helpers/date-utils';
import { logError } from '@/common/helpers/error-handling';
import * as measurementUtils from '@/common/helpers/measurements-utilities';
import {
  AvailableUnitMetrics,
  ChartData,
  ChartInputData,
  DISCONNECTION_ERRORS,
  DisconnectionMetricData,
  DisconnectionsResults,
  MetricResultsData,
  OldSkippedTestsResults,
  ScheduledTestsResultsForDay,
  UnitAnalyticsSkippedTestsResults
} from '@/features/unit-tests-view/types';
import { dateParse, EventBus, Http } from '@te/cdev-utils';
import { getUnitAnalyticsChartData } from '../helpers/UnitTests.helpers';

export const DOWNLOAD_METRICS = [
  MetricKeys.UDP_DOWNLOAD,
  MetricKeys.DOWNLOAD_MT,
  MetricKeys.DOWNLOAD_ST
];
export const UPLOAD_METRICS = [
  MetricKeys.UDP_UPLOAD,
  MetricKeys.UPLOAD_MT,
  MetricKeys.UPLOAD_ST
];

const SPEED_METRICS = [...DOWNLOAD_METRICS, ...UPLOAD_METRICS];
const LATENCY_METRICS = [MetricKeys.LATENCY, MetricKeys.PACKET_LOSS];

export const getNewYValue = (item: DisconnectionMetricData): number | null => {
  // If item passed isn't required type then log error and fail gracefully
  if (!item || typeof item !== 'object') {
    logError(new Error(`argument passed is not of expected type: ${item}`));
    return null;
  }

  // If no metricValue then it is currently an ongoing disconnection so we calculate the duration from start to now
  const itemValue = item.metricValue;
  const startTime = item.dtime;

  // If we don't have required data then log error and fail gracefully
  if (!itemValue && !startTime) {
    logError(
      new Error(
        `No metricValue or dtime which are required: ${JSON.stringify(item)}`
      )
    );
    return null;
  }

  // If not a number then log error and fail gracefully
  if (itemValue && typeof itemValue !== 'number') {
    logError(new Error(`MetricValue not a number: ${JSON.stringify(item)}`));
    return null;
  }

  const durationTillNow =
    dateUtils.durationToDate(startTime, Date.now()) * 1000;
  return !itemValue ? durationTillNow : itemValue;
};

export const getNewXValue = (item: DisconnectionMetricData): Date => {
  // for disconnections response so use updated key
  let endTime = item?.endDtime;
  const isOngoingDisconnection = !endTime;

  // If endDtime is empty then it is an ongoing disconnection and we need to display that
  // TODO - there could potentially be odd visuals due to timezone issues by using browsers now time.
  endTime = isOngoingDisconnection ? dateUtils.formatDate(Date.now()) : endTime;
  return dateParse(endTime);
};

export const transformNewDisconnectionsData = (
  highchartsData: ChartData[]
): ChartData[] => {
  if (!Array.isArray(highchartsData)) {
    logError(new Error(`Expected array: ${JSON.stringify(highchartsData)}`));
    return [];
  }

  return highchartsData.map((topLevelData) => {
    const { data } = topLevelData;

    if (!Array.isArray(data)) {
      logError(new Error(`Expected array: ${JSON.stringify(data)}`));
      return topLevelData;
    }

    const updatedData = data.map((innerData) => {
      const { data: apiData } = innerData;
      const endDtime = apiData?.endDtime || null;
      const isOngoingDisconnection = !endDtime;
      const newYVal = getNewYValue(apiData as DisconnectionMetricData);
      const newXVal = getNewXValue(apiData as DisconnectionMetricData);
      innerData.y = newYVal;
      innerData.x = newXVal;
      innerData.isOngoingDisconnection = isOngoingDisconnection;
      return innerData;
    });

    topLevelData.data = updatedData;
    return topLevelData;
  });
};

export async function getUnitDisconnections(
  formData: MeasurementBuilderFormData[],
  unitId: number,
  params?: Record<string, string>
): Promise<PromiseResponse<DisconnectionsResults | Error>> {
  const requestArray = measurementUtils.getRequestDataArray(formData);
  const requestObj = requestArray[0];

  requestObj.requestData = {
    ...requestObj.requestData,
    date: {
      from: dateUtils.relativeDateToDate(requestObj.requestData.date.from),
      to: dateUtils.relativeDateToDate(requestObj.requestData.date.to)
    }
  };

  try {
    // TODO: Fix the type in the next line if you can
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiResponse = await Http.get<any>(
      config.api.unitAnalytics,
      `/${unitId}/disconnections`,
      params
    );
    // rename 'results' property to 'metricData'
    apiResponse.data['metricData'] = apiResponse.data['results'];
    delete apiResponse.data['results'];
    const dataError: DISCONNECTION_ERRORS = apiResponse.data?.error;

    // convert data object to array
    const { dataSources, metricMetadata, dataUsage, metricData, error } =
      apiResponse.data;
    const apiData = [
      { dataSources, metricMetadata, dataUsage, metricData, error }
    ];
    apiResponse.data = apiData;
    const responseData = apiResponse;
    const data = { ...requestObj, responseData };

    if (DISCONNECTION_ERRORS[dataError]) {
      return {
        data: data.responseData.data
      };
    }

    // Resolve diffs between what the front-end expects and the backend sends
    const transformedData = measurementUtils.transformData([data]);

    // Merge responses together
    const mergedResponses = measurementUtils.consolidateResponses(
      transformedData,
      formData
    );

    // Convert data into Highcharts-readable data
    let highchartsData = dataToSeries(mergedResponses, formData);
    highchartsData = transformNewDisconnectionsData(highchartsData);

    return {
      data: highchartsData
    };
  } catch (error) {
    return Promise.reject(error);
  }
}

export function getSkippedTests(
  unitId: string,
  params?: Record<string, string>
): Promise<PromiseResponse<OldSkippedTestsResults>> {
  return Http.get(config.api.analytics, `units/${unitId}/breaches`, params);
}

export function getUnitAnalyticsSkippedTests(
  unitId: string,
  params?: Record<string, string>
): Promise<PromiseResponse<UnitAnalyticsSkippedTestsResults>> {
  return Http.get(config.api.unitAnalytics, `/${unitId}/skipped_tests`, params);
}

interface GetScheduledTestsArgs {
  unitId: number;
  date: string;
  metric: MetricKeys;
}

export function getScheduledTestsForDay(data: GetScheduledTestsArgs): Promise<
  PromiseResponse<{
    results: ScheduledTestsResultsForDay[];
  }>
> {
  const { unitId, date, metric } = data || {};
  const params = {
    date,
    metric,
    localTargetSetResultsOnly: '1'
  };

  return Http.get(
    config.api.unitAnalytics,
    `${unitId}/scheduled_tests_daily`,
    params
  );
}

// NEW API
export async function getMetricData(config: {
  url: string;
  requestBody: Record<string, unknown>;
  metric: Partial<Metric> | MetricKeys;
  signal?: AbortSignal;
}): Promise<MetricResultsData> {
  const { url, requestBody, metric, signal } = config;

  const response = await Http.request<PromiseResponse<MetricResultsData>>(url, {
    method: 'POST',
    body: JSON.stringify({ ...requestBody, metric }),
    headers: new Headers({
      'Content-Type': 'application/json'
    }),
    signal: signal
  });

  return response.data;
}

const getDailyScheduledTestsRequests: Record<string, AbortController> = {};

const abortAllDailyScheduledTestsRequests = (): void => {
  const keys = Object.keys(getDailyScheduledTestsRequests);
  keys.forEach((key) => {
    const controller = getDailyScheduledTestsRequests[key];
    if (controller) {
      controller.abort();
      delete getDailyScheduledTestsRequests[key];
    }
  });
};

export async function getDailyScheduledTests(
  unitId: number,
  hasRestrictedMetrics: boolean,
  data: Record<string, string>,
  timePeriod: string,
  aggregation: string,
  abortAll?: boolean
): Promise<MetricResultsData[]> {
  if (abortAll) {
    abortAllDailyScheduledTestsRequests();
  }

  const abortController = new AbortController();
  const signal = abortController.signal;
  const requestId = `${unitId}_${timePeriod}_${
    data.to
  }_${new Date().toISOString()}`;
  getDailyScheduledTestsRequests[requestId] = abortController;

  const url = `${config.api.unitAnalytics}/${unitId}/metric_results`;

  const requestBody = {
    date: {
      from: data.from,
      to: data.to
    },
    includeAggregated: true,
    includeTotal: true,
    localTargetSetResultsOnly: true,
    aggregation
  };

  const fetchMetricData = (metric: MetricKeys): Promise<MetricResultsData> =>
    getMetricData({
      url,
      requestBody,
      metric,
      signal
    });

  const speedMetrics = (hasRestrictedMetrics ? [] : SPEED_METRICS).map(
    fetchMetricData
  );
  const latencyMetrics = LATENCY_METRICS.map(fetchMetricData);

  const fetchedSpeedMetrics = Promise.all(speedMetrics).then(
    selectSpeedMetricsFromAvailableResults
  );
  const fetchedLatencyMetrics = Promise.all(latencyMetrics);

  try {
    const [speedMetricData, latencyMetricData] = await Promise.all([
      fetchedSpeedMetrics,
      fetchedLatencyMetrics
    ]);
    const metrics = [...speedMetricData, ...latencyMetricData].filter(Boolean);

    for (const metricData of metrics) {
      EventBus.$emit('data-usage', {
        endpoint: 'metric_results',
        dataUsed: metricData?.dataUsage?.totalBytesProcessed,
        metric: metricData?.metricMetadata?.metricKey || '',
        timePeriod
      });
    }

    return metrics;
  } finally {
    delete getDailyScheduledTestsRequests[requestId];
  }
}

/**
 * From a list of metrics, which we know are not empty, determine which speed metrics we want to show to the user.
 */
export function getSpeedMetricsFromAvailableMetrics(metrics: MetricKeys[]): {
  download:
    | MetricKeys.UDP_DOWNLOAD
    | MetricKeys.DOWNLOAD_MT
    | MetricKeys.DOWNLOAD_ST;
  /* prettier-ignore */ // we want the same format for DL and UL
  upload:
    | MetricKeys.UDP_UPLOAD
    | MetricKeys.UPLOAD_MT
    | MetricKeys.UPLOAD_ST;
} {
  if (hasMetrics(MetricKeys.UDP_DOWNLOAD, MetricKeys.UDP_UPLOAD)) {
    return {
      download: MetricKeys.UDP_DOWNLOAD,
      upload: MetricKeys.UDP_UPLOAD
    };
  }

  if (
    hasMetrics(
      MetricKeys.DOWNLOAD_MT,
      MetricKeys.DOWNLOAD_ST,
      MetricKeys.UPLOAD_MT,
      MetricKeys.UPLOAD_ST
    )
  ) {
    return {
      download: inOrderOfPreference(
        MetricKeys.DOWNLOAD_MT,
        MetricKeys.DOWNLOAD_ST
      ),
      /* prettier-ignore */ // Format the same for upload and download
      upload: inOrderOfPreference(
        MetricKeys.UPLOAD_MT,
        MetricKeys.UPLOAD_ST
      )
    };
  }

  // Fallback option: No metrics found at all, so return UDP
  return {
    download: MetricKeys.UDP_DOWNLOAD,
    upload: MetricKeys.UDP_UPLOAD
  };

  function hasMetrics(...keys: MetricKeys[]): boolean {
    return !!keys.find((key) => metrics.includes(key));
  }

  function inOrderOfPreference<T extends MetricKeys>(
    fallbackKey: T,
    ...remainingKeys: T[]
  ): T {
    for (const key of [fallbackKey, ...remainingKeys]) {
      if (metrics.includes(key)) {
        return key;
      }
    }
    return fallbackKey;
  }
}

/**
 * From a list of all loaded metrics, returns the speed metrics (upload/download).
 * Looks at only the nonempty metrics and determines which are the highest-priority ones to show.
 */
function selectSpeedMetricsFromAvailableResults(
  loadedMetricData: MetricResultsData[]
): MetricResultsData[] {
  const nonEmptyResultsByMetric: Map<MetricKeys, MetricResultsData> = new Map(
    loadedMetricData
      .filter(dataIsNonempty)
      .map((metricData) => [
        /* key */ metricData.metricMetadata.metricKey as MetricKeys,
        /* value */ metricData
      ])
  );

  const speedMetrics = getSpeedMetricsFromAvailableMetrics([
    ...nonEmptyResultsByMetric.keys()
  ]);

  return [
    nonEmptyResultsByMetric.get(speedMetrics.download),
    nonEmptyResultsByMetric.get(speedMetrics.upload)
  ];

  function dataIsNonempty(metricData: MetricResultsData): boolean {
    if (!metricData) {
      return false;
    }
    const { aggregatedDaily, aggregatedHourly, total } = metricData;
    return (
      typeof total?.metricValue === 'number' ||
      aggregatedDaily?.length > 0 ||
      aggregatedHourly?.length > 0
    );
  }
}

const fetchMetricResults = async (
  formData: MeasurementBuilderFormData,
  unitId: number,
  timePeriod: string,
  includeTotal = false
): Promise<{
  data: MetricResultsData;
  metricKey: Partial<Metric> | MetricKeys;
  formData: MeasurementBuilderFormData;
}> => {
  const url = `${config.api.unitAnalytics}/${unitId}/metric_results`;
  const requestBody = {
    date: formData?.date,
    includeAggregated: true,
    aggregation: formData?.timeAggregation,
    includeTotal: includeTotal,
    localTargetSetResultsOnly: true
  };

  const data = await getMetricData({
    url,
    requestBody,
    metric: formData?.metric
  });

  EventBus.$emit('data-usage', {
    endpoint: 'metric_results',
    dataUsed: data?.dataUsage?.totalBytesProcessed,
    metric: formData?.metric || '',
    timePeriod
  });

  return {
    data: data,
    metricKey: formData?.metric,
    formData
  };
};

export const getMetricResultsChartData = async (
  formData: MeasurementBuilderFormData,
  unitId: number,
  timePeriod: string
): Promise<ChartInputData> => {
  const includeTotal = true;
  const metricData = await fetchMetricResults(
    formData,
    unitId,
    timePeriod,
    includeTotal
  );
  return getUnitAnalyticsChartData(
    metricData?.data,
    metricData?.metricKey as string,
    [metricData?.formData]
  );
};

export const getAvailableUnitMetrics = (
  unitId: number
): Promise<PromiseResponse<AvailableUnitMetrics>> => {
  return Http.get<PromiseResponse<AvailableUnitMetrics>>(
    config.api.unitAnalytics,
    `${unitId}/metrics`
  ).then((data) => {
    data.data.metrics.forEach((metric) => {
      metric.type = measurementUtils.realTypeToUsefulType(metric.type);
    });

    return data;
  });
};
